import type { Post } from "@/lib/news/types";
import hero from "@/assets/news/snabblan-swish-samma-dag.jpg";

const SWISH_SNABB = [
  "ferratum", "credifi", "lumify", "saldo", "creditstar", "loanstep",
  "flexkonto", "merax", "tomly", "brixokk", "northmill", "klaralan",
];

const NAST_BAST = ["lendo", "sambla", "enklare", "advisa", "axo"];

export const SNABBLAN_SWISH_SAMMA_DAG: Record<string, Post> = {
  "snabblan-direkt-utbetalning-swish-samma-dag": {
    title: "Snabblån med direkt utbetalning via Swish samma dag – jämförelse 2026",
    intro:
      "Behöver du pengar idag är det inte räntan du tänker på först – det är hur snabbt de landar på kontot. Ett snabblån med direkt utbetalning via Swish samma dag kan vara framme inom minuter, men bara om långivaren, klockslaget och din bank samverkar. Här jämför vi alla långivare som betalar ut samma dag 2026, med ränta, avgifter, utbetalningstid och krav – och vi visar exakt vad ett snabblån kostar innan du signerar.",
    author: "Erik Lindqvist",
    authorTitle: "Ekonomiredaktör",
    published: "2026-08-28",
    updated: "2026-08-28",
    heroSrc: hero,
    heroAlt: "Kvinna tar emot utbetalning från snabblån via Swish samma dag i mobilen",
    topLenderId: "ferratum",
    bottomLenderId: "credifi",
    topLenderTitle: "Bäst i test – snabblån med utbetalning samma dag",
    bottomLenderTitle: "Vår rekommendation för utbetalning via Swish",
    reviewsLenderId: "credifi",
    cta: { to: "/snabblan", label: "Jämför snabblån med direkt utbetalning" },
    calculator: {
      title: "Räkna på snabblån med direkt utbetalning",
      intro:
        "Innan pengarna kommer är det värt tjugo sekunder att se vad de kostar. Dra i reglagen och få månadskostnad, räntekostnad, total lånekostnad och effektiv ränta.",
      note:
        "Månadskostnad, effektiv ränta, total lånekostnad och löptid hänger ihop. Ett snabblån på 10 000 kr som återbetalas på 6 månader kostar betydligt mindre totalt än samma belopp på 36 månader, även om månadskostnaden känns tuffare. Jämför alltid effektiv ränta mellan långivare – där ingår uppläggningsavgift och aviavgift.",
      amount: 10000,
      minAmount: 1000,
      maxAmount: 50000,
      months: 12,
      maxMonths: 60,
      rate: 23.9,
      minRate: 5,
      maxRate: 39,
      setupFee: 295,
      invoiceFee: 29,
    },
    faq: [
      {
        q: "Kan jag verkligen få ett snabblån utbetalt via Swish samma dag?",
        a: "Ja. Flera långivare betalar ut via Swish eller realtidsbetalning direkt efter godkänd ansökan och BankID-signering. Ansöker du på vardagar mellan 08 och 20 är pengarna oftast framme inom några minuter till en timme.",
      },
      {
        q: "Vad krävs för att utbetalningen ska gå på minuter?",
        a: "Att långivaren stödjer realtidsbetalning, att du signerar med BankID, att kontot står i ditt eget namn och att din bank tar emot realtidsinsättningar. Faller något av dessa bort sker utbetalningen i stället nästa bankdag.",
      },
      {
        q: "Fungerar det på kvällar och helger?",
        a: "Ofta, men inte alltid. Långivare som använder realtidsbetalning klarar helger, medan de som använder vanliga bankfiler betalar ut nästa bankdag. Kontrollera alltid utbetalningstiden i villkoren innan du ansöker på en söndag.",
      },
      {
        q: "Tar långivaren UC vid snabblån?",
        a: "Många snabblånegivare använder Creditsafe eller Dun & Bradstreet i stället för UC. Kreditprövningen görs ändå, alltid, enligt konsumentkreditlagen – skillnaden är bara vilket upplysningsbolag som används.",
      },
      {
        q: "Vad kostar ett snabblån på 10 000 kr?",
        a: "Med 23,9 procent nominell ränta, 295 kr uppläggningsavgift och 29 kr aviavgift blir månadskostnaden cirka 975 kr på 12 månader. Totalt betalar du då ungefär 11 700 kr, vilket motsvarar en effektiv ränta kring 33 procent.",
      },
      {
        q: "Kan jag betala tillbaka i förtid?",
        a: "Ja, alltid och utan extra kostnad. Räntan beräknas på kvarvarande skuld, så varje extra amortering sänker din totalkostnad direkt.",
      },
      {
        q: "Vad händer om jag inte kan betala en avi?",
        a: "Kontakta långivaren innan förfallodagen. De flesta erbjuder anstånd eller en ny betalningsplan. Uteblir betalningen tillkommer påminnelseavgift och dröjsmålsränta, och efter inkasso kan ärendet gå vidare till Kronofogden.",
      },
      {
        q: "Finns det billigare alternativ när det är akut?",
        a: "Ofta. Avbetalningsplan hos fordringsägaren, en befintlig kontokredit eller ett lån av närstående är nästan alltid billigare. Ett snabblån är rimligast när behovet är litet, tillfälligt och du vet att du kan lösa det inom några månader.",
      },
    ],
    blocks: [
      { t: "h2", text: "Snabblån med direkt utbetalning via Swish – kort svar" },
      { t: "p", text: "Ja, det går att få ett snabblån utbetalt samma dag, ofta inom minuter. Förutsättningen är att du ansöker hos en långivare som använder realtidsbetalning, signerar med BankID och har ett konto i eget namn i en bank som tar emot omedelbara insättningar." },
      { t: "p", text: "Vi vet att den här sidan ofta läses i ett stressat läge. Därför lägger vi siffrorna och villkoren öppet, utan pekpinnar, så att du kan fatta ett beslut du känner dig trygg med även imorgon." },
      {
        t: "stats",
        items: [
          { label: "Vanlig utbetalningstid", value: "5–60 minuter" },
          { label: "Beloppsspann", value: "1 000–50 000 kr" },
          { label: "Typisk ränta 2026", value: "12–29 %" },
        ],
      },

      { t: "h2", text: "Så fungerar utbetalning via Swish och realtidsbetalning" },
      { t: "p", text: "Swish bygger på det svenska realtidssystemet för betalningar. När långivaren godkänt din ansökan skickas pengarna som en omedelbar betalning i stället för via en vanlig bankfil, som annars hanteras i klumpar några gånger per dygn." },
      { t: "h3", text: "Skillnaden mot vanlig bankbetalning" },
      { t: "p", text: "En vanlig banköverföring mellan olika banker tar en bankdag. En realtidsbetalning når mottagaren på sekunder, dygnet runt, även på röda dagar. Det är därför utbetalningstiden skiljer sig så kraftigt mellan långivare som annars ser likadana ut." },
      { t: "h3", text: "Vad som kan bromsa utbetalningen" },
      {
        t: "ul",
        items: [
          "Manuell handläggning vid större belopp eller oklara uppgifter.",
          "Konto som inte står i ditt eget namn.",
          "Kompletterande inkomstunderlag som efterfrågas.",
          "Ansökan som skickas in sent på kvällen hos en långivare utan realtidsstöd.",
        ],
      },

      { t: "h2", text: "Bäst i test – snabblån med direkt utbetalning samma dag" },
      { t: "lenders", ids: SWISH_SNABB, title: "Alla långivare med snabb utbetalning 2026", note: "Räntan sätts individuellt efter kreditprövning. Räkneexemplen är typexempel enligt konsumentkreditlagen." },

      { t: "h2", text: "Näst bästa alternativen – förmedlare med en kreditupplysning" },
      { t: "p", text: "Är behovet inte akut inom timmen är en låneförmedlare nästan alltid billigare. Du får bud från upp till 40 banker till priset av en enda kreditupplysning, och pengarna kommer normalt inom ett till två dygn." },
      { t: "lenders", ids: NAST_BAST, title: "Förmedlare med lägre ränta men något längre handläggning" },

      { t: "h2", text: "Utbetalningstider hos de snabbaste långivarna" },
      {
        t: "table",
        caption: "Typisk tid från godkänd ansökan till pengar på kontot (vardag, kontortid)",
        head: ["Utbetalningssätt", "Typisk tid", "Fungerar på helg", "Kommentar"],
        rows: [
          ["Swish / realtidsbetalning", "5–15 minuter", "Ja", "Kräver konto i eget namn"],
          ["Realtid till annan bank", "15–60 minuter", "Ja", "Beroende på mottagarbank"],
          ["Vanlig bankfil", "Nästa bankdag", "Nej", "Vanligast vid större belopp"],
          ["Manuell handläggning", "1–2 bankdagar", "Nej", "Vid komplettering av underlag"],
        ],
      },

      { t: "h2", text: "Vad kostar ett snabblån 2026?" },
      { t: "p", text: "Nedan visar vi tre typexempel med 23,9 procent nominell ränta, 295 kr uppläggningsavgift och 29 kr aviavgift. Effektiv ränta inkluderar samtliga avgifter." },
      {
        t: "table",
        caption: "Räkneexempel: annuitetslån, 23,9 % nominell ränta",
        head: ["Belopp", "Löptid", "Månadskostnad", "Total kostnad", "Effektiv ränta"],
        rows: [
          ["5 000 kr", "6 mån", "922 kr", "5 533 kr", "38,4 %"],
          ["10 000 kr", "12 mån", "975 kr", "11 700 kr", "33,1 %"],
          ["20 000 kr", "24 mån", "1 084 kr", "26 016 kr", "29,4 %"],
          ["30 000 kr", "36 mån", "1 205 kr", "43 380 kr", "27,8 %"],
        ],
      },
      { t: "p", text: "Testa dina egna siffror i vår [lånekalkyl](/guider/lanekalkyl) innan du signerar. Att se totalbeloppet i kronor gör beslutet betydligt enklare än att jämföra procenttal." },

      { t: "h2", text: "Räntetak och kostnadstak – ditt skydd i lagen" },
      { t: "p", text: "Den nominella räntan får aldrig överstiga Riksbankens referensränta plus 20 procentenheter. De samlade kostnaderna för räntor, avgifter och dröjsmålsränta får aldrig överstiga själva lånebeloppet. Lånar du 10 000 kr kan kostnaden alltså aldrig bli mer än 10 000 kr, oavsett hur länge skulden ligger kvar." },
      {
        t: "box",
        tone: "info",
        title: "Det här gäller alltid",
        items: [
          "14 dagars ångerrätt enligt konsumentkreditlagen.",
          "Rätt att lösa lånet i förtid utan extra avgift.",
          "Kreditprövning är obligatorisk – ingen seriös långivare hoppar över den.",
          "Långivaren måste ha tillstånd från Finansinspektionen.",
        ],
      },

      { t: "h2", text: "Krav för att bli beviljad" },
      {
        t: "ul",
        items: [
          "Minst 18 år, flera långivare kräver 20 eller 21 år.",
          "Folkbokförd i Sverige med svenskt personnummer.",
          "Mobilt BankID för signering.",
          "Regelbunden inkomst som går att styrka.",
          "Bankkonto i eget namn för utbetalningen.",
        ],
      },
      { t: "h3", text: "Om du har betalningsanmärkning" },
      { t: "p", text: "Flera snabblånegivare gör en individuell bedömning även med anmärkning, särskilt om skulden är reglerad och inkomsten stabil. Läs mer på vår sida om [lån med betalningsanmärkning](/lan-med-betalningsanmarkning-bast-i-test-30000-kr)." },

      { t: "h2", text: "Så ansöker du – steg för steg" },
      {
        t: "steps",
        title: "Från ansökan till pengar på kontot",
        items: [
          "Välj belopp och löptid och räkna igenom kostnaden.",
          "Fyll i ansökan med inkomst, boende och försörjningsbörda.",
          "Signera med BankID – kreditprövningen tar sekunder.",
          "Få besked, ofta direkt på skärmen.",
          "Pengarna betalas ut via Swish eller realtidsbetalning.",
        ],
      },

      { t: "h2", text: "För- och nackdelar med snabblån samma dag" },
      {
        t: "proscons",
        pros: [
          "Pengar på kontot inom minuter",
          "Helt digital ansökan dygnet runt",
          "Ingen säkerhet krävs",
          "Flera långivare prövar utan UC",
        ],
        cons: [
          "Högre ränta än privatlån",
          "Kort löptid ger hög månadskostnad",
          "Lägre maxbelopp",
          "Risk att lösa ett problem med ett nytt",
        ],
      },

      { t: "h2", text: "Snabblån eller privatlån – vad passar dig?" },
      {
        t: "table",
        caption: "Jämförelse mellan lånetyperna",
        head: ["", "Snabblån", "Privatlån"],
        rows: [
          ["Belopp", "1 000–50 000 kr", "10 000–600 000 kr"],
          ["Utbetalning", "Minuter", "1–3 bankdagar"],
          ["Ränta", "12–29 %", "4,95–24 %"],
          ["Kreditupplysning", "Ofta Creditsafe", "Oftast UC"],
          ["Passar för", "Akut och litet behov", "Planerade köp och samling"],
        ],
      },
      { t: "p", text: "Är beloppet större än 50 000 kr eller behovet planerat, jämför i stället [privatlån med lägst ränta](/privatlan-jamforelse-lagst-ranta-2026)." },

      { t: "h2", text: "Vanliga misstag när det är bråttom" },
      {
        t: "box",
        tone: "warning",
        title: "Fyra fällor att undvika",
        items: [
          "Att söka hos fem långivare samtidigt och samla på sig kreditupplysningar.",
          "Att välja längsta löptiden för att månadskostnaden ska se låg ut.",
          "Att jämföra nominell ränta i stället för effektiv ränta.",
          "Att missa att aviavgiften försvinner med e-faktura hos många långivare.",
        ],
      },

      { t: "h2", text: "Billigare alternativ som är värda en minut" },
      {
        t: "ul",
        items: [
          "Avbetalningsplan direkt hos fordringsägaren, ofta räntefritt.",
          "En befintlig kontokredit eller kort med lägre ränta.",
          "Samla dyra krediter i ett [samlingslån](/samla-lan).",
          "Kommunens budget- och skuldrådgivning, som alltid är kostnadsfri.",
        ],
      },

      { t: "h2", text: "Så granskar vi långivarna" },
      { t: "p", text: "Vi väger ränta, avgifter, utbetalningstid, transparens i villkoren, tillstånd hos Finansinspektionen och kundomdömen. Ingen långivare kan köpa sig till en placering. Läs mer om [hur vi granskar](/om-oss/hur-vi-granskar) och om vår [redaktion](/om-oss/redaktion)." },

      { t: "h2", text: "Sammanfattning" },
      { t: "p", text: "Ett snabblån med direkt utbetalning via Swish löser ett akut behov samma dag, men kostnaden är märkbart högre än vid ett vanligt privatlån. Välj kortast möjliga löptid, jämför effektiv ränta, kontrollera att långivaren har tillstånd – och lös lånet i förtid när du kan." },

      { t: "h2", text: "Källor" },
      {
        t: "ul",
        items: [
          "[Finansinspektionen](https://www.fi.se) – tillståndsregister och tillsyn",
          "[Konsumentverket](https://www.konsumentverket.se) – räntetak, kostnadstak och ångerrätt",
          "[Riksbanken](https://www.riksbank.se) – referensränta och realtidsbetalningar",
          "[Kronofogden](https://www.kronofogden.se) – vad som händer vid utebliven betalning",
        ],
      },
    ],
  },
};
